import "@/App.css";
// Build trigger: 2026-05-22 LIVE Stripe migration (force frontend rebuild for pk_live_)
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import { LanguageProvider } from "@/contexts/LanguageContext";
import { HelmetProvider } from "react-helmet-async";
import AnalyticsLoader from "@/components/AnalyticsLoader";

// Pages
import LandingPage from "@/pages/LandingPage";
import PassportDetailPage from "@/pages/PassportDetailPage";
import AttractionDetailPage from "@/pages/AttractionDetailPage";
import AboutPage from "@/pages/AboutPage";
import ComoFuncionaPage from "@/pages/ComoFuncionaPage";
import TermosPage from "@/pages/TermosPage";
import PrivacidadePage from "@/pages/PrivacidadePage";
import CookiesPage from "@/pages/CookiesPage";
import CookieBanner from "@/components/CookieBanner";
import LoginPage from "@/pages/LoginPage";
import RegisterPage from "@/pages/RegisterPage";
import CheckoutSuccessPage from "@/pages/CheckoutSuccessPage";
import CheckoutCancelPage from "@/pages/CheckoutCancelPage";
import CustomerDashboard from "@/pages/CustomerDashboard";
import PartnerDashboard from "@/pages/PartnerDashboard";
import AdminDashboard from "@/pages/AdminDashboard";
import TwoFactorSetupPage from "@/pages/TwoFactorSetupPage";

// Protected Route Component
const ProtectedRoute = ({ children, allowedRoles }) => {
  const { user, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen bg-navy-dark flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-gold"></div>
      </div>
    );
  }
  
  if (!user) {
    return <Navigate to="/login" replace />;
  }
  
  if (allowedRoles && !allowedRoles.includes(user.role)) {
    // Redirect based on role
    if (user.role === "admin") return <Navigate to="/admin" replace />;
    if (user.role === "partner") return <Navigate to="/partner" replace />;
    return <Navigate to="/dashboard" replace />;
  }
  
  return children;
};

function AppRoutes() {
  const { user } = useAuth();

  // Helper to get the correct dashboard based on role
  const getDashboardPath = () => {
    if (!user) return "/login";
    if (user.role === "admin") return "/admin";
    if (user.role === "partner") return "/partner";
    return "/dashboard";
  };

  return (
    <Routes>
      {/* Public Routes */}
      <Route path="/" element={<LandingPage />} />
      <Route path="/passaporte/:id" element={<PassportDetailPage />} />
      <Route path="/passport/:id" element={<PassportDetailPage />} />
      <Route path="/atracao/:id" element={<AttractionDetailPage />} />
      <Route path="/experiencias/:id" element={<AttractionDetailPage />} />
      <Route path="/experiences/:id" element={<AttractionDetailPage />} />
      <Route path="/attraction/:id" element={<AttractionDetailPage />} />
      <Route path="/sobre" element={<AboutPage />} />
      <Route path="/about" element={<AboutPage />} />
      <Route path="/como-funciona" element={<ComoFuncionaPage />} />
      <Route path="/how-it-works" element={<ComoFuncionaPage />} />
      <Route path="/termos" element={<TermosPage />} />
      <Route path="/terms" element={<TermosPage />} />
      <Route path="/privacidade" element={<PrivacidadePage />} />
      <Route path="/privacy" element={<PrivacidadePage />} />
      <Route path="/cookies" element={<CookiesPage />} />
      <Route path="/cookie-policy" element={<CookiesPage />} />
      {/* English URL versions (/en/...) */}
      <Route path="/en" element={<LandingPage />} />
      <Route path="/en/passaporte/:id" element={<PassportDetailPage />} />
      <Route path="/en/passport/:id" element={<PassportDetailPage />} />
      <Route path="/en/atracao/:id" element={<AttractionDetailPage />} />
      <Route path="/en/experiencias/:id" element={<AttractionDetailPage />} />
      <Route path="/en/experiences/:id" element={<AttractionDetailPage />} />
      <Route path="/en/attraction/:id" element={<AttractionDetailPage />} />
      <Route path="/en/sobre" element={<AboutPage />} />
      <Route path="/en/about" element={<AboutPage />} />
      <Route path="/en/como-funciona" element={<ComoFuncionaPage />} />
      <Route path="/en/how-it-works" element={<ComoFuncionaPage />} />
      <Route path="/en/termos" element={<TermosPage />} />
      <Route path="/en/terms" element={<TermosPage />} />
      <Route path="/en/privacidade" element={<PrivacidadePage />} />
      <Route path="/en/privacy" element={<PrivacidadePage />} />
      <Route path="/en/cookies" element={<CookiesPage />} />
      <Route path="/en/cookie-policy" element={<CookiesPage />} />
      <Route path="/login" element={user ? <Navigate to={getDashboardPath()} replace /> : <LoginPage />} />
      <Route path="/register" element={user ? <Navigate to={getDashboardPath()} replace /> : <RegisterPage />} />
      <Route path="/checkout/success" element={<CheckoutSuccessPage />} />
      <Route path="/checkout/cancel" element={<CheckoutCancelPage />} />
      <Route path="/admin/2fa-setup" element={<TwoFactorSetupPage />} />
      
      {/* Customer Dashboard */}
      <Route 
        path="/dashboard/*" 
        element={
          <ProtectedRoute allowedRoles={["customer", "admin"]}>
            <CustomerDashboard />
          </ProtectedRoute>
        } 
      />
      
      {/* Partner Dashboard */}
      <Route 
        path="/partner/*" 
        element={
          <ProtectedRoute allowedRoles={["partner", "admin"]}>
            <PartnerDashboard />
          </ProtectedRoute>
        } 
      />
      
      {/* Admin Dashboard */}
      <Route 
        path="/admin/*" 
        element={
          <ProtectedRoute allowedRoles={["admin"]}>
            <AdminDashboard />
          </ProtectedRoute>
        } 
      />
      
      {/* Fallback */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <HelmetProvider>
      <BrowserRouter>
        <LanguageProvider>
          <AuthProvider>
            <AnalyticsLoader />
            <AppRoutes />
            <CookieBanner />
            <Toaster position="top-right" richColors />
          </AuthProvider>
        </LanguageProvider>
      </BrowserRouter>
    </HelmetProvider>
  );
}

export default App;
